import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useCart, MIN_ORDER } from "../context/CartContext";
import { Reveal } from "../components/store/Reveal";
import { OrderSummary } from "../components/store/OrderSummary";
import { CheckoutFields } from "../components/store/CheckoutFields";

export default function Checkout() {
  const { items, total } = useCart();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: "", email: "", phone: "", address: "", city: "", cap: "", province: "", notes: "", delivery_date: "", gift: false, gift_from: "", gift_message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [promo, setPromo] = useState(null);
  const isB2B = !!sessionStorage.getItem("b2b_token");
  const minOrder = isB2B ? parseFloat(sessionStorage.getItem("b2b_min_order")) || MIN_ORDER : MIN_ORDER;
  const discountAmount = promo ? total * (promo.percent / 100) : 0;
  const grandTotal = total - discountAmount;
  const missing = Math.max(0, minOrder - grandTotal);
  const minDate = new Date(Date.now() + 2 * 86400000).toISOString().slice(0, 10);

  const submit = async (e) => {
    e.preventDefault();
    if (items.length === 0) return toast.error("Il carrello è vuoto");
    if (missing > 0) return toast.error(`Ordine minimo ${minOrder.toFixed(0)}€ — mancano ${missing.toFixed(2)}€`);
    setSubmitting(true);
    try {
      const b2bToken = sessionStorage.getItem("b2b_token");
      const res = await api.post("/checkout", {
        items: items.map((i) => ({ slug: i.slug, quantity: i.quantity })),
        customer: form,
        origin_url: window.location.origin,
        discount_code: !isB2B && promo ? promo.code : "",
      }, b2bToken ? { headers: { Authorization: `Bearer ${b2bToken}` } } : undefined);
      window.location.href = res.data.checkout_url;
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore durante il checkout");
      setSubmitting(false);
    }
  };

  if (items.length === 0)
    return (
      <div className="max-w-4xl mx-auto px-6 py-24 text-center">
        <p className="text-[#78716C]">Il carrello è vuoto.</p>
        <button onClick={() => navigate("/prodotti")} className="mt-6 btn-tuscan px-8 py-3.5 rounded-full text-sm font-semibold">Vai al menu</button>
      </div>
    );

  return (
    <div data-testid="checkout-page" className="max-w-6xl mx-auto px-4 sm:px-8 py-12 lg:py-16">
      <Link to="/prodotti" className="inline-flex items-center gap-2 text-sm text-[#78716C] hover:text-[#2E8A57] transition-colors mb-8">
        <ArrowLeft className="w-4 h-4" /> Continua gli acquisti
      </Link>
      <Reveal>
        <h1 className="font-serif-display font-bold text-3xl sm:text-4xl tracking-tight text-[#1C1917] mb-4">Checkout ospite</h1>
        {isB2B && (
          <p data-testid="checkout-b2b-badge" className="inline-flex items-center rounded-full bg-[#2E8A57]/10 text-[#2E8A57] px-4 py-2 text-xs font-semibold mb-8">
            Account rivenditore attivo — i prezzi B2B vengono applicati al pagamento
          </p>
        )}
      </Reveal>
      <div className="grid lg:grid-cols-5 gap-12">
        <form onSubmit={submit} className="lg:col-span-3 grid grid-cols-2 gap-5">
          <CheckoutFields form={form} setForm={setForm} minDate={minDate} />
          <button
            data-testid="checkout-pay-btn"
            type="submit"
            disabled={submitting || missing > 0}
            className="col-span-2 btn-tuscan py-4.5 rounded-full font-semibold text-sm tracking-wide py-4 disabled:opacity-40 disabled:pointer-events-none inline-flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" /> {submitting ? "Reindirizzamento a Stripe..." : `Paga con carta — ${grandTotal.toFixed(2).replace(".", ",")} €`}
          </button>
          <p className="col-span-2 text-xs text-[#78716C] text-center">Pagamento sicuro con Stripe · Spedizione refrigerata inclusa sopra i 100€</p>
        </form>

        <div className="lg:col-span-2">
          <OrderSummary
            items={items}
            isB2B={isB2B}
            promo={promo}
            setPromo={setPromo}
            total={total}
            discountAmount={discountAmount}
            grandTotal={grandTotal}
            missing={missing}
            minOrder={minOrder}
          />
        </div>
      </div>
    </div>
  );
}
